import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router'
import { toBlob } from 'html-to-image';
import { axiosClient } from '../../data/axios';
import type { Game, TeamSummary } from '../../types';
import { queryClient } from '../../main';
import {
    getTotalGold,
    getTotalKda,
    sortParticipantsByTeamAndRole,
} from '../../utils/team';
import { getFormattedDateString } from '../../utils/date';
import { DamageByRoleChart } from '../../components/games/damageByRole';
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    Grid,
    Skeleton,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { StatisticsTable } from '../../components/games/statisticsTable';
import { PingsByGameChart } from '../../components/games/pingsByGame';
import { Awards } from '../../components/games/awards';
import {
    BLUE,
    RED,
    ColumnLabels,
    TeamDetailSection,
    TeamHeader,
    formatDuration,
} from '../../components/games/history';

export const Route = createFileRoute('/games/$id')({
    component: RouteComponent,
    loader: async ({ params }) => {
        await queryClient.ensureQueryData({
            queryKey: ['game', params.id],
            queryFn: () => getGameById(params.id)
        })
    },
    errorComponent: ErrorComponent
})

const getGameById = (id: string) => axiosClient.get<Game>(`/games/${id}`).then(r => r.data)

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Typography
        sx={{
            color: "#6c7280",
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            mb: 1.5,
        }}
    >
        {children}
    </Typography>
);

type SnackbarState = {
    open: boolean;
    message: string;
    severity: 'success' | 'error';
};

const CARD_BACKGROUND = '#0d1117';

const cardOptions = {
    backgroundColor: CARD_BACKGROUND,
    pixelRatio: 2,
    cacheBust: true,
};

function RouteComponent() {
    const { id } = useParams({ from: '/games/$id' });
    const navigate = useNavigate();

    const cardRef = useRef<HTMLDivElement>(null);
    const [busy, setBusy] = useState(false);
    const [snackbar, setSnackbar] = useState<SnackbarState>({
        open: false,
        message: '',
        severity: 'success',
    });

    const { data, isSuccess, isLoading } = useQuery<Game>({
        queryKey: ['game', id],
        queryFn: () => getGameById(id)
    });

    const notify = (message: string, severity: 'success' | 'error') =>
        setSnackbar({ open: true, message, severity });

    const handleDownload = async () => {
        if (!cardRef.current || !data) return;
        setBusy(true);
        try {
            const blob = await toBlob(cardRef.current, cardOptions);
            if (!blob) throw new Error('Empty blob');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `staturdaynight-${data.id.slice(0, 8)}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 1000);
            notify('Match card downloaded', 'success');
        } catch (err) {
            console.error(err);
            notify('Could not generate match card', 'error');
        } finally {
            setBusy(false);
        }
    };

    const handleCopy = async () => {
        if (!cardRef.current) return;
        setBusy(true);
        try {
            const blob = await toBlob(cardRef.current, cardOptions);
            if (!blob) throw new Error('Empty blob');
            await navigator.clipboard.write([
                new ClipboardItem({ 'image/png': blob }),
            ]);
            notify('Match card copied — paste it anywhere', 'success');
        } catch (err) {
            console.error(err);
            notify(
                'Could not copy (browser may not support image clipboard)',
                'error',
            );
        } finally {
            setBusy(false);
        }
    };

    if (isLoading) {
        return (
            <Container maxWidth="lg" sx={{ py: 3 }}>
                <Skeleton variant="rounded" height={120} sx={{ bgcolor: '#161b22', borderRadius: 2, mb: 3 }} />
                <Skeleton variant="rounded" height={240} sx={{ bgcolor: '#161b22', borderRadius: 2 }} />
            </Container>
        );
    }

    if (!isSuccess) return <ErrorComponent />;

    const grouped = sortParticipantsByTeamAndRole(data.playerStats);
    const blueKda = getTotalKda(grouped.BLUE);
    const redKda = getTotalKda(grouped.RED);
    const blueGold = getTotalGold(grouped.BLUE);
    const redGold = getTotalGold(grouped.RED);

    const blueSummary: TeamSummary = {
        champions: grouped.BLUE.map((p) => p.champion.name),
        kills: blueKda.kills,
        deaths: blueKda.deaths,
        assists: blueKda.assists,
        goldEarned: blueGold,
    };
    const redSummary: TeamSummary = {
        champions: grouped.RED.map((p) => p.champion.name),
        kills: redKda.kills,
        deaths: redKda.deaths,
        assists: redKda.assists,
        goldEarned: redGold,
    };

    const winnerColor = data.winner === 'RED' ? RED : BLUE;
    const blueWon = data.winner === 'BLUE';
    const redWon = data.winner === 'RED';

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2 }}
            >
                <Button
                    onClick={() => navigate({ to: '/' })}
                    startIcon={<ArrowBackIcon />}
                    sx={{ color: '#9ba1b0', textTransform: 'none' }}
                >
                    Back to matches
                </Button>
                <Stack direction="row" spacing={1}>
                    <Button
                        onClick={handleCopy}
                        disabled={busy}
                        startIcon={<ContentCopyIcon />}
                        variant="outlined"
                        size="small"
                        sx={{
                            color: '#9ba1b0',
                            borderColor: '#2a3140',
                            textTransform: 'none',
                            '&:hover': { borderColor: '#0a97ab', color: '#e6e8ee' },
                        }}
                    >
                        Copy
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={busy}
                        startIcon={<DownloadIcon />}
                        variant="outlined"
                        size="small"
                        sx={{
                            color: '#9ba1b0',
                            borderColor: '#2a3140',
                            textTransform: 'none',
                            '&:hover': { borderColor: '#0a97ab', color: '#e6e8ee' },
                        }}
                    >
                        Download
                    </Button>
                </Stack>
            </Stack>

            {/* MATCH HEADER */}
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: 2,
                    backgroundColor: '#161b22',
                    border: '1px solid #2a3140',
                    p: 2,
                    mb: 3,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        backgroundColor: winnerColor,
                    },
                }}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={{ pl: 1.5 }}
                >
                    <TeamHeader
                        label={blueWon ? 'VICTORY' : 'DEFEAT'}
                        color={BLUE}
                        team={blueSummary}
                        align="left"
                    />

                    <Typography
                        sx={{
                            color: '#3a4252',
                            fontSize: 12,
                            letterSpacing: 2,
                            fontWeight: 700,
                            flexShrink: 0,
                        }}
                    >
                        VS
                    </Typography>

                    <TeamHeader
                        label={redWon ? 'VICTORY' : 'DEFEAT'}
                        color={RED}
                        team={redSummary}
                        align="right"
                    />

                    <Divider orientation="vertical" flexItem sx={{ borderColor: '#2a3140', mx: 1 }} />

                    <Stack alignItems="flex-end" spacing={0.25} sx={{ minWidth: 100 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                            <AccessTimeIcon sx={{ color: '#9ba1b0', fontSize: 14 }} />
                            <Typography
                                sx={{
                                    color: '#e6e8ee',
                                    fontSize: 13,
                                    fontFamily: "'Roboto Mono', monospace",
                                }}
                            >
                                {formatDuration(data.duration)}
                            </Typography>
                        </Stack>
                        <Typography sx={{ color: '#9ba1b0', fontSize: 11 }}>
                            {getFormattedDateString(data.date)}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            <Awards gameId={data.id} />

            {/* TEAM ROWS */}
            <Box
                ref={cardRef}
                sx={{
                    backgroundColor: '#161b22',
                    border: '1px solid #2a3140',
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                }}
            >
                <ColumnLabels />
                <Stack spacing={2}>
                    <TeamDetailSection
                        label="BLUE SIDE"
                        color={BLUE}
                        summary={blueSummary}
                        players={grouped.BLUE}
                        gameId={data.id}
                    />
                    <TeamDetailSection
                        label="RED SIDE"
                        color={RED}
                        summary={redSummary}
                        players={grouped.RED}
                        gameId={data.id}
                    />
                </Stack>
            </Box>

            {/* CHARTS */}
            <SectionTitle>Charts</SectionTitle>
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DamageByRoleChart teamStats={grouped} />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <PingsByGameChart data={grouped} />
                </Grid>
            </Grid>

            {/* DETAILED STATS TABLE */}
            <SectionTitle>All stats</SectionTitle>
            <StatisticsTable data={grouped} />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3500}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    )
}

function ErrorComponent() {
    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Typography sx={{ color: '#9ba1b0' }}>The data could not be fetched.</Typography>
        </Container>
    );
}
